import { Plus, Search } from "lucide-react";
import { AnimatePresence } from "motion/react";
import { memo, useCallback, useState } from "react";

import { AddCardDialogContent } from "@/components/AddCardDialogContent";
import { DeckCard } from "@/components/DeckCard";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Doc, Id } from "@convex/_generated/dataModel";

export interface DeckCardsProps {
  cards?: Doc<"cards">[];
  cardCount: number;
  deckId: Id<"decks">;
}

interface SortOption {
  sortFunction: (a: Doc<"cards">, b: Doc<"cards">) => number;
  displayName: string;
}

const sortOptions = {
  "recently-created": {
    sortFunction: (a, b) => b._creationTime - a._creationTime,
    displayName: "Recently created",
  },
  "alphabetical-question": {
    sortFunction: (a, b) => a.question.toLowerCase().localeCompare(b.question.toLowerCase()),
    displayName: "Alphabetical (question)",
  },
  "alphabetical-answer": {
    sortFunction: (a, b) => a.answer.toLowerCase().localeCompare(b.answer.toLowerCase()),
    displayName: "Alphabetical (answer)",
  },
  "next-review": {
    sortFunction: (a, b) => a.nextReview - b.nextReview,
    displayName: "Next review",
  },
} satisfies Record<string, SortOption>;

type SortOptionKey = keyof typeof sortOptions;

const sortOptionKeys = Object.keys(sortOptions) as SortOptionKey[];

export const DeckCards = memo(({ cards, deckId, cardCount }: DeckCardsProps) => {
  const [filterString, setFilterString] = useState<string>("");
  const [sortOption, setSortOption] = useState<SortOptionKey>("recently-created");
  const [addCardDialogOpen, setAddCardDialogOpen] = useState(false);
  const closeAddCardDialog = useCallback(() => setAddCardDialogOpen(false), []);

  const filteredCardsOrPlaceholders =
    cards?.filter((card) => {
      const question = card.question.toLowerCase();
      const answer = card.answer.toLowerCase();
      const filter = filterString.toLowerCase();
      return question.includes(filter) || answer.includes(filter);
    }) ?? (Array(cardCount).fill(null) as null[]);

  // Sort the filtered cards based on the selected sort option
  const sortedCardsOrPlaceholders = filteredCardsOrPlaceholders.sort((a, b) => {
    if (a === null || b === null) return 0;
    const { sortFunction } = sortOptions[sortOption];
    return sortFunction(a, b);
  });

  const renderedCardsOrPlaceholders = sortedCardsOrPlaceholders.map((card, i) => (
    <DeckCard
      key={card?._id ?? i}
      id={card?._id}
      deckId={deckId}
      question={card?.question}
      answer={card?.answer}
      singleSided={card?.singleSided}
    />
  ));

  const arePlaceholders = !cards;

  return (
    <>
      <h2 className="text-xl font-semibold max-sm:mb-4">
        Cards ({filteredCardsOrPlaceholders.length})
      </h2>

      <div className="flex flex-col sm:flex-row justify-between gap-4 sm:items-end mb-4">
        <div className="flex">
          <Dialog open={addCardDialogOpen} onOpenChange={setAddCardDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-1" />
                ADD CARD
              </Button>
            </DialogTrigger>
            <AddCardDialogContent closeDialog={closeAddCardDialog} />
          </Dialog>
        </div>

        <div className="flex items-end gap-2">
          <SelectGroup>
            <SelectLabel className="text-xs font-medium text-gray-600">Sort by</SelectLabel>
            <Select
              defaultValue="recently-created"
              onValueChange={(value) => setSortOption(value as SortOptionKey)}
            >
              <SelectTrigger id="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {sortOptionKeys.map((key) => (
                  <SelectItem key={key} value={key}>
                    {sortOptions[key].displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SelectGroup>

          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Filter cards"
              className="pl-9 w-full max-w-[200px]"
              value={filterString}
              onChange={(e) => setFilterString(e.target.value)}
            />
          </div>
        </div>
      </div>

      {filteredCardsOrPlaceholders.length > 0 ? (
        <div className="mt-6 p-4 bg-gray-50 rounded">
          <div className="flex flex-wrap gap-4 relative">
            {arePlaceholders ? (
              renderedCardsOrPlaceholders
            ) : (
              <AnimatePresence initial={false} mode="popLayout">
                {renderedCardsOrPlaceholders}
              </AnimatePresence>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-8 text-center p-8 bg-gray-50 rounded">
          <p className="text-gray-600 mb-2">This deck doesn't have any cards.</p>
          <p className="text-gray-600">Press "Add Card" to create your first.</p>
        </div>
      )}
    </>
  );
});
